/**
 * 청구서 생성/수정 팝업 컴포넌트
 * 청구서의 기본 정보와 세부 내역을 입력하고 관리하는 모달 컴포넌트입니다.
 *
 * 주요 기능:
 * - 청구서 기본 정보 입력/수정
 * - 청구서 세부 내역 관리
 * - 건물 및 스케줄 선택
 * - 공급가, 부가세, 총액 계산
 * - 청구서 저장/삭제
 */

import _ from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getScheduleDetail_RCB01, mntBillUpdate, mntGetWeekDay } from "src/api/billingSchedule/billingSchedule-api";
import { useApiOperation } from "src/api/hooks";
import { getProviderList } from "src/api/provider/provider-api";
import { BaseButton, BaseModal, BaseSelect } from "src/components";
import { BaseAbstractModal } from "src/components/BaseAbstractModal";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import { useToast } from "src/recoil/toast/hook";
import { ViewYmdFormat } from "src/utils";
import BillBasicInfo from "./BillBasicInfo";
import BillDetailListInfo, { BillDetail } from "./BillDetailListInfo";

/**
 * 청구서 생성/수정 팝업 컴포넌트
 * @param isChange - 수정 모드 여부
 * @param contractDetail - 계약 상세 정보
 * @param showBtn - 버튼 표시 여부
 * @param bill - 청구서 정보
 * @param setBill - 청구서 정보 업데이트 함수
 * @param onClose - 팝업 닫기 핸들러
 * @param onClick - 클릭 이벤트 핸들러
 * @param onConfirm - 확인 이벤트 핸들러
 * @param messageModal - 메시지 모달 상태
 * @param setMessageModal - 메시지 모달 상태 업데이트 함수
 * @param getRounds - 라운드 정보 조회 함수
 * @param allBill - 전체 청구서 목록
 * @param buildingId - 건물 ID
 * @param scheduleId - 스케줄 ID
 * @param deletePop - 삭제 팝업 표시 여부
 */
/**
 * 변경 여부 판단에 사용하는 bill 필드.
 * 모달에서 사용자가 직접 편집할 수 있는 항목만 추린다.
 * (기본정보 입력 항목 + 건물/스케줄 선택)
 */
const EDITABLE_BILL_KEYS = [
  "billTitle",
  "billStartTime",
  "billEndTime",
  "billIssueDate",
  "billDueDate",
  "pgcode",
  "taxInvoiceIssueDate",
  "billDescription",
  "amountDescription",
  "buildingId",
  "scheduleId",
];

/**
 * 변경 여부 비교용으로 bill 에서 편집 항목만 추린다.
 * buildingId/scheduleId 는 응답에서는 문자열, 화면 로직에서는 숫자로 들어오는 경우가 섞여 있어
 * 형식을 맞춰야 값이 같은데도 변경으로 잡히지 않는다.
 */
const pickEditable = (bill: any) => {
  const picked: any = _.pick(bill, EDITABLE_BILL_KEYS);
  ["buildingId", "scheduleId"].forEach((key) => {
    if (picked[key] !== null && picked[key] !== undefined) {
      picked[key] = String(picked[key]);
    }
  });
  return picked;
};

/** 서버에서 받은 세부 내역을 화면 편집용 형태로 변환 */
const toDetailList = (billDetailList: any): Array<BillDetail> =>
  billDetailList?.map((detail: any) => ({
    ...detail,
    isAutoTaxUse: false,
    checkTaxAmount: detail.taxAmount > 0 ? "tax" : "nontax",
  })) || [];

const MakeBillPopup = ({
  isChange,
  contractDetail,
  showBtn,
  bill,
  setBill,
  onClose,
  onClick,
  onConfirm,
  messageModal,
  setMessageModal,
  getRounds,
  allBill,
  buildingId,
  scheduleId,
  deletePop,
}: any) => {
  const { openToast } = useToast();
  const { isAuthority } = usePartnerAuthority();
  const { executeAsync: getScheduleList } = useApiOperation(getScheduleDetail_RCB01);
  const { executeAsync: getWeekDay } = useApiOperation(mntGetWeekDay);
  const { executeAsync: updateBill } = useApiOperation(mntBillUpdate);
  const [delFlag, setDelFlag] = useState(false);
  //  프로바이더 목록 api
  const { executeAsync: getProviders } = useApiOperation(getProviderList);
  const [selectedBuildingId, setSelectedBuildingId] = useState<any>(null);
  const [selectedScheduleId, setSelectedScheduleId] = useState<any>(null);
  const [firstSelector, setFirstSelector] = useState<any>([]);
  const [secondSelector, setSecondSelector] = useState<any>([]);
  const [detailList, setDetailList] = useState<Array<BillDetail>>([]);
  const [taxModal, setTaxModal] = useState(false);
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    btnTitle: string[];
    title: string;
    message: string;
    successMessage: string;
    type: "confirm" | "close" | "save";
  }>({
    isOpen: false,
    btnTitle: ["취소", "확인"],
    title: "",
    message: "",
    successMessage: "",
    type: "confirm",
  });

  const prevBillDetailListRef = useRef<any>(null);

  /** 모달을 연 시점(저장 후에는 마지막 저장 시점)의 편집값. 변경 여부 판단 기준 */
  const baselineRef = useRef<{ bill: any; detailList: Array<BillDetail> } | null>(null);

  /** 현재 편집값 */
  const currentSnapshot = useCallback(() => ({ bill: pickEditable(bill), detailList }), [bill, detailList]);

  /** 기준 시점 대비 편집된 내용이 있는지 */
  const isDirty = useCallback(() => !_.isEqual(baselineRef.current, currentSnapshot()), [currentSnapshot]);

  /** 저장이 반영된 뒤 기준값을 현재 값으로 갱신 */
  const resetBaseline = useCallback(() => {
    baselineRef.current = currentSnapshot();
  }, [currentSnapshot]);

  // 다른 청구서를 열면 기준값을 새로 잡는다
  useEffect(() => {
    baselineRef.current = {
      bill: pickEditable(bill),
      detailList: toDetailList(bill.billDetailList),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bill.billId]);

  useEffect(() => {
    // 실제로 내용이 바뀌었을 때만 실행
    if (!_.isEqual(prevBillDetailListRef.current, bill.billDetailList)) {
      const details = toDetailList(bill.billDetailList);
      setDetailList(details);
      prevBillDetailListRef.current = bill.billDetailList;

      // 서버 데이터가 들어온 시점(최초 로드 / 저장 후 재조회)의 값을 기준값으로 맞춘다.
      // billDetailList 는 사용자 편집으로는 바뀌지 않고 재조회로만 바뀌므로,
      // 여기서 갱신해야 저장 직후 재조회 결과와 기준값이 어긋나지 않는다.
      baselineRef.current = { bill: pickEditable(bill), detailList: details };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bill.billDetailList]);

  /**
   * 공급가 총액 계산
   * 삭제되지 않은 항목들의 공급가 합계를 계산합니다.
   */
  const supplyTotal = useMemo(() => {
    let sum = 0;
    if (detailList!.length > 0) {
      detailList.forEach((detail: any) => {
        if (!detail.isDeleted) {
          sum = sum + detail.supplyAmount;
        }
      });
    }
    return sum;
  }, [detailList]);
  /**
   * 부가세 총액 계산
   * 삭제되지 않은 항목들의 부가세 합계를 계산합니다.
   */
  const taxTotal = useMemo(() => {
    let sum = 0;
    if (detailList!.length > 0) {
      detailList.forEach((detail: any) => {
        if (!detail.isDeleted) {
          sum = sum + detail.taxAmount;
        }
      });
    }
    return sum;
  }, [detailList]);
  /**
   * 총액 계산
   * 공급가 총액과 부가세 총액의 합계를 계산합니다.
   */
  const priceTotal = useMemo(() => {
    const totalAmount = supplyTotal + taxTotal;

    return totalAmount;
  }, [supplyTotal, taxTotal]);

  /**
   * 선택한 건물의 스케줄에 등록된 프로바이더 목록 조회
   * @param providerIds - 콤마로 구분된 프로바이더 ID 문자열
   * @returns 프로바이더 목록
   */
  const getProvidersName = useCallback(
    async (providerIds: string) => {
      const getPds = await getProviders({ providerId: String(providerIds) });
      // console.log("getProviders", getPds);
      return getPds.data.data.content;
    },
    [getProviders],
  );

  /**
   * 선택한 건물의 관리비 스케줄 목록 조회
   * @param buildingId - 건물 ID
   * @param scheduleId - 스케줄 ID (선택적)
   */
  const findMntScheduleIds = async (buildingId: string, scheduleId?: string) => {
    if (contractDetail) {
      const params = {
        contractId: Number(contractDetail!.contract!.contractId),
        buildingId: Number(buildingId),
        supplyType: "MNT",
      };
      const list = await getScheduleList(params);
      // setTableData(list.data.data.scheduleList.content);
      // console.log("list", list.data.data.scheduleList);
      if (list.data.data.scheduleList && list.data.data.scheduleList.length > 0) {
        let pvIds = list.data.data.scheduleList.map((schedule: any) => schedule.providerId).join(",");

        //받아온 목록에서 프로바이더 명 매핑처리
        const providers = await getProvidersName(pvIds);
        if (providers.length > 0) {
          let mntScheduleList = list.data.data.scheduleList;
          const second: any = [];
          mntScheduleList.forEach((msl: any) => {
            const pd = providers.find((pv: any) => msl.providerId === pv.providerId);
            const opt = {
              label: pd?.providerName + "(" + pd?.providerId + ")",
              value: msl.scheduleId,
            };
            second.push(opt);
          });
          setSecondSelector(second);
          const resolvedScheduleId = scheduleId ? Number(scheduleId) : Number(second[0]!.value);
          if (resolvedScheduleId) {
            setSelectedScheduleId(String(resolvedScheduleId));
          }

          setBill((prevBill: any) => {
            const temp = _.cloneDeep(prevBill);
            temp.scheduleId = resolvedScheduleId;

            if (!temp.pgcode) {
              const targetSchedule = mntScheduleList.find((s: any) => String(s.scheduleId) === String(resolvedScheduleId));
              if (targetSchedule?.regularPgcode) {
                temp.pgcode = targetSchedule.regularPgcode;
              }
            }

            return temp;
          });
        }
      }
    }
  };
  /**
   * 청구서 삭제 처리
   * 청구서를 삭제하고 결과를 메시지로 표시합니다.
   */
  const handleSendMail = useCallback(async () => {
    await updateBill({ scheduleId: scheduleId, body: { billId: bill.billId, isDeleted: true } });
    setDelFlag(false);
    setMessageModal({ ...messageModal, isOpen: true, message: "삭제 되었습니다." });
    await getRounds(bill.scheduleId);
  }, [updateBill, scheduleId, bill, setMessageModal, messageModal, getRounds]);

  useEffect(() => {
    // setDetailList([]);
    if (contractDetail.product) {
      const buildingSelectData: any = [];

      //상품 ID로 상품안의 건물 데이터
      const snapshotProduct = contractDetail.product;

      snapshotProduct!.buildings!.map((building: any) => {
        // console.log("building", building);
        const option = {
          label: building.initValue + "(" + building.domainId + ")",
          value: building.domainId,
          providerId: 0,
        };
        buildingSelectData.push(option);
      });
      // console.log("buildingSelectData[0]!.value", buildingSelectData[0]!.value);
      setFirstSelector(buildingSelectData);
      findMntScheduleIds(bill.buildingId, bill.scheduleId);
      if (bill && bill.billId > 0) {
        //수정 모드이기 때문에 데이터 세팅 해준다.
        console.log("ID가 있다.");

        if (bill.billDetailList && bill.billDetailList.length > 0) {
          bill.billDetailList.forEach((detail: any) => {
            detail.isAutoTaxUse = false;

            if (detail.taxAmount > 0) {
              detail.checkTaxAmount = "tax";
            } else {
              detail.checkTaxAmount = "nontax";
            }
          });
        }

        setSelectedBuildingId(String(bill.buildingId));
        setSelectedScheduleId(String(bill.scheduleId));

        setDetailList(bill.billDetailList);
      } else {
        const temp = _.cloneDeep(bill);
        if (buildingId) {
          temp.buildingId = buildingId;
        } else {
          temp.buildingId = Number(buildingSelectData[0]!.value!);
        }
        setSelectedBuildingId(temp.buildingId);

        if (!temp.pgcode && contractDetail?.scheduleList) {
          const schedule = contractDetail.scheduleList.find((s: any) => String(s.scheduleId) === String(scheduleId));
          if (schedule?.regularPgcode) {
            temp.pgcode = schedule.regularPgcode;
          }
        }

        if (scheduleId) {
          findMntScheduleIds(temp.buildingId, scheduleId);
        } else {
          findMntScheduleIds(temp.buildingId);
        }

        setBill(temp);
        // 신규 생성은 여기서 건물/결제수단 기본값이 채워지므로, 그 상태를 기준값으로 삼는다
        baselineRef.current = { bill: pickEditable(temp), detailList: [] };
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractDetail.product]);
  /**
   * 기본 유효성 검사
   * 청구서 저장 전 필수 항목 검사를 수행합니다.
   * @param bill - 검사할 청구서 정보
   * @returns 유효성 검사 결과 메시지 (성공 시 null)
   */
  const isValidBasicInfo = (bill: any) => {
    if (bill.billTitle === "") {
      openToast({ content: "청구서명은 필수입니다.", isSystemToast: true });
      return false;
    }
    if (bill.billStartTime === "") {
      openToast({ content: "산출기간 시작일은 필수입니다.", isSystemToast: true });
      return false;
    }
    if (bill.billEndTime === "") {
      openToast({ content: "산출기간 종료일은 필수입니다.", isSystemToast: true });
      return false;
    }
    if (bill.billIssueDate === "") {
      openToast({ content: "납부기간 시작일은 필수입니다.", isSystemToast: true });
      return false;
    }
    if (bill.billDueDate === "") {
      openToast({ content: "납부기간 종료일은 필수입니다.", isSystemToast: true });
      return false;
    }
    if (bill.pgcode !== "creditcard" && (bill.taxInvoiceIssueDate === null || bill.taxInvoiceIssueDate === "")) {
      openToast({ content: "계산서 작성일은 필수입니다.", isSystemToast: true });
      return false;
    }
    if (bill.pgcode === "") {
      openToast({ content: "결제수단은 필수입니다.", isSystemToast: true });
      return false;
    }

    return true;
  };

  /**
   * 청구서 데이터 유효성 검사
   * 청구서 확정 전 세부 내역의 유효성을 검사합니다.
   *
   * 검사 항목:
   * - 세부내역 최소 1건 이상 등록
   * - 항목명 필수 입력
   * - 공급가와 부가세의 합이 소계와 일치
   * - 과세 항목의 경우 부가세가 공급가의 10%인지 확인
   * - 소계 총합이 0원 이상
   *
   * @param bill - 검사할 청구서 정보
   * @returns 유효성 검사 결과 (true: 오류 있음, false: 오류 없음)
   */
  const isValidBillInfo = useCallback(
    (bill: any) => {
      let sum = 0;
      if (bill) {
        if (detailList && detailList.length === 0) {
          openToast({ content: "세부내역은 1건이상 등록되어야 합니다.", isSystemToast: true });
          return false;
        } else {
          try {
            detailList.forEach((detail: any) => {
              if (!detail.isDeleted) {
                sum = sum + detail.totalAmount;
              }
              if (!detail.isDeleted && (detail.itemName === "" || detail.itemName === null)) {
                openToast({ content: "항목명은 필수 입니다.", isSystemToast: true });
                throw Error;
              }
              if (!detail.isDeleted && Number(detail.supplyAmount + detail.taxAmount) !== Number(detail.totalAmount)) {
                openToast({ content: detail.itemName + "의 공급가와 부가세의 합이 소계와 맞지 않습니다.", isSystemToast: true });
                throw Error;
              }

              if (!detail.isDeleted && detail.checkTaxAmount === "tax") {
                if (Number(detail.taxAmount) === 0) {
                  openToast({ content: "부가세는 공급가의 10%로 입력해 주세요.", isSystemToast: true });
                  throw Error;
                }
                if (
                  Number(detail.supplyAmount * 0.1) - 1 > Number(detail.taxAmount) ||
                  Number(detail.taxAmount) > Number(detail.supplyAmount * 0.1) + 1
                ) {
                  openToast({ content: "부가세는 공급가의 10%로 입력해 주세요.", isSystemToast: true });
                  throw Error;
                }
              }
            });
          } catch (error) {
            return false;
          }
        }
      }
      return true;
    },
    [openToast, detailList],
  );

  const changeConfirm = () => {
    const temp = _.cloneDeep(bill);
    temp.isConfirmed = !temp.isConfirmed;
    setBill(temp);
  };
  const confirmPopup = useCallback(() => {
    let confirmTrue = "해제 시 청구서를 수정할 수 있습니다.";
    let confirmFalse = priceTotal === 0 ? "0원으로 청구서를 확정 승인하시겠습니까?" : "작성한 내용으로 청구서를 확정 승인하시겠습니까?";
    setModalState((prev) => ({
      ...prev,
      isOpen: true,
      title: priceTotal === 0 ? "청구 금액이 0원입니다." : bill.isConfirmed ? "확정 해제하시겠습니까?" : "확정하시겠습니까?",
      btnTitle: ["취소", "확인"],
      message: bill.isConfirmed ? confirmTrue : confirmFalse,
      type: "confirm",
      successMessage: bill.isConfirmed ? "해제되었습니다." : "확정되었습니다.",
    }));
  }, [bill.isConfirmed, priceTotal]);

  const closePopup = useCallback(() => {
    // 저장 후이거나 편집한 내용이 없으면 경고 없이 닫는다
    if (!isDirty()) {
      onClose();
      return;
    }

    setModalState((prev) => ({
      ...prev,
      isOpen: true,
      title: "페이지를 나가시겠습니까?",
      btnTitle: ["취소", "나가기"],
      message: "변경사항이 저장되지 않을 수 있습니다.",
      type: "close",
    }));
  }, [isDirty, onClose]);

  const savePopup = useCallback(() => {
    setModalState((prev) => ({
      ...prev,
      isOpen: true,
      title: "저장하시겠습니까?",
      btnTitle: ["취소", "확인"],
      message: "작성한 내용으로 청구서를 저장하시겠습니까?",
      type: "save",
      successMessage: "저장되었습니다.",
    }));
  }, []);

  return (
    <>
      <BaseAbstractModal size="xlarge" isOpen={isChange} className="billing-schedule-info-modal">
        <article className="base-abstract-modal__contents">
          <section className="base-abstract-modal__title border-bottom">
            <h1>청구서 {showBtn ? (bill?.billId > 0 ? "수정" : "생성") : "상세"}</h1>
          </section>

          {/* 건물 및 스케줄 선택 */}
          {contractDetail?.product?.productType === "TIME_COURT" && (
            <div className="flex-center mt10 px30">
              <BaseSelect
                value={selectedBuildingId || null}
                setStateValue={(buildingId: string) => {
                  setSelectedBuildingId(buildingId);
                  findMntScheduleIds(buildingId);
                  const temp = _.cloneDeep(bill);
                  temp.buildingId = Number(buildingId);
                  setBill(temp);
                }}
                stateOptions={firstSelector}
                className="mr8 minmax260"
                isDisabled={true}
              />
              <BaseSelect
                value={selectedScheduleId || null}
                setStateValue={(scheduleId: string) => {
                  setSelectedScheduleId(String(scheduleId));
                  const temp = _.cloneDeep(bill);
                  temp.scheduleId = Number(scheduleId);
                  setBill(temp);
                }}
                stateOptions={secondSelector}
                className="minmax260"
                isDisabled={true}
              />
            </div>
          )}
          <div className="flex-center mt10 px30">
            <p className="pr30 font14 text-gray400">* 청구서 상세 항목 작성 및 확정은 납부시작일 전일까지 완료해 주세요.</p>
          </div>
          {/* 기본정보 */}
          <BillBasicInfo bill={bill} setBill={setBill} showBtn={showBtn} priceTotal={priceTotal} />
          {/* 세부내역 */}
          <BillDetailListInfo
            bill={bill}
            setBill={setBill}
            showBtn={showBtn}
            detailList={detailList}
            setDetailList={setDetailList}
            supplyTotal={supplyTotal}
            taxTotal={taxTotal}
            priceTotal={priceTotal}
          />
          <div className="base-abstract-modal__btn-wrap flex-center-between">
            <div className="flex-row flex-center start">
              {showBtn && Number(bill?.billId) > 0 && (
                <>
                  {isAuthority("w", "CONTRACT") && (
                    <button
                      className={`base-btn ${bill?.isConfirmed ? "color-white" : "color-blue"}`}
                      onClick={() => {
                        bill?.billId ? confirmPopup() : changeConfirm();
                      }}
                      disabled={
                        bill.billPayStatus !== "TEMP_SAVE" ||
                        allBill?.billEtc?.receiptCount > 0 ||
                        allBill?.billEtc?.taxInvoiceCount > 0 ||
                        allBill?.billEtc?.chargeEmailSentAt !== ""
                      }
                    >
                      {bill?.isConfirmed ? "확정해제" : "확정"}
                    </button>
                  )}
                  {Number(bill?.billId) > 0 && bill?.isConfirmed === false && isAuthority("d", "CONTRACT") && (
                    <BaseButton
                      title="삭제"
                      className="ml8 color-white"
                      disabled={bill.isConfirmed || bill.billPayStatus !== "TEMP_SAVE"}
                      onClick={() => setDelFlag(true)}
                    />
                  )}
                </>
              )}
              {delFlag && (
                <BaseModal
                  isOpen={delFlag}
                  btnLeftTitle={"취소"}
                  btnRightTitle={"삭제"}
                  onClose={() => {
                    setDelFlag(false);
                  }}
                  onClick={handleSendMail}
                  title={"삭제하시겠습니까?"}
                >
                  <p>{"청구서를 삭제하시겠습니까?"}</p>
                </BaseModal>
              )}
            </div>
            {showBtn && !bill?.isConfirmed ? (
              <div className="flex-row flex-center start">
                <button className="base-btn color-white mr8" onClick={() => closePopup()}>
                  닫기
                </button>
                {isAuthority("w", "CONTRACT") && (
                  <button className="base-btn" onClick={() => savePopup()}>
                    저장
                  </button>
                )}
              </div>
            ) : (
              <div className="flex-row flex-center start">
                <button className="base-btn mr8" onClick={() => onClose()}>
                  확인
                </button>
              </div>
            )}
          </div>
        </article>
        <BaseModal
          isOpen={modalState.isOpen}
          btnLeftTitle={modalState?.btnTitle[0]!}
          btnRightTitle={modalState?.btnTitle[1]!}
          onClose={() => {
            setModalState({ ...modalState, isOpen: false });
          }}
          onClick={async () => {
            setModalState({ ...modalState, isOpen: false });
            if (modalState.type === "confirm") {
              const temp = _.cloneDeep(bill);

              if (bill.isConfirmed === false) {
                let isValid = isValidBillInfo(temp);

                if (!isValid) {
                  return;
                } else {
                  const today = moment();
                  const response = await getWeekDay({ checkDate: moment(today).format("YYYYMMDD") });

                  const useableDate = moment(response.data.data.weekday).format(ViewYmdFormat.FULL);
                  //휴무일시 적용 해야함 오늘 날짜 기준으로 휴무일시체크

                  const ym = moment(temp.taxInvoiceIssueDate).format("YYYY.MM");

                  const addMd = moment(ym).add(1, "months").add(10, "days");

                  const check = moment(addMd).format(ViewYmdFormat.FULL);

                  if (moment(useableDate).isSameOrAfter(moment(check))) {
                    setTaxModal(true);
                  } else {
                    const saved = await onConfirm(detailList);
                    // 연체현황 확정/확정해제
                    if (saved) {
                      resetBaseline();
                      await getRounds(bill.scheduleId);
                      setMessageModal({ ...messageModal, message: modalState.successMessage, isOpen: true });
                      //harry 수정
                      // await onClose();
                    }
                  }
                }
              } else {
                const saved = await onConfirm();
                if (saved) {
                  resetBaseline();
                  await getRounds(bill.scheduleId);

                  setMessageModal({ ...messageModal, message: modalState.successMessage, isOpen: true });
                  //harry 수정
                  // await onClose();
                }
              }
            }

            // 모달 취소버튼 > 닫기
            if (modalState.type === "close") {
              await onClose();
            }
            if (modalState.type === "save") {
              // 청구서 생성 / 이용료 빌링 수정
              const temp = _.cloneDeep(bill);
              const isValid = isValidBasicInfo(temp);

              if (!isValid) {
                return;
              } else {
                const saved = await onClick(detailList);
                if (saved) {
                  resetBaseline();
                  await getRounds(bill.scheduleId);
                  setMessageModal({ ...messageModal, message: modalState.successMessage, isOpen: true });

                  // 신규 생성이면 팝업 닫기
                  if (temp.billId === 0) {
                    await onClose();
                  }
                }
              }
            }
          }}
          title={modalState.title}
        >
          <p>{modalState.message}</p>
        </BaseModal>
        {taxModal && (
          <BaseModal
            isOpen={taxModal}
            btnRightTitle={"확인"}
            onClick={async () => {
              setTaxModal(false);
              const saved = await onConfirm(detailList);
              if (saved) {
                resetBaseline();
                setMessageModal({ ...messageModal, message: modalState.successMessage, isOpen: true });
                //harry 수정
                // await onClose();
              }
            }}
            title={"가산세 부과 안내"}
          >
            <p>선택한 작성일자로 세금계산서 또는 계산서를 발행하면 가산세가 부과될 수 있으니 참고해 주세요</p>
          </BaseModal>
        )}
      </BaseAbstractModal>
    </>
  );
};
export default MakeBillPopup;
